import { supabase } from '../lib/supabase';

class MaterialCategoryService {
  // Get all categories
  async getAllCategories() {
    try {
      const { data, error } = await supabase
        ?.from('material_categories')
        ?.select('*')
        ?.order('level', { ascending: true })
        ?.order('display_order', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching material categories:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get categories by level
  async getCategoriesByLevel(level) {
    try {
      const { data, error } = await supabase
        ?.from('material_categories')
        ?.select('*')
        ?.eq('level', level)
        ?.order('display_order', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching categories by level:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get categories by parent
  async getCategoriesByParent(parentId) {
    try {
      const query = supabase
        ?.from('material_categories')
        ?.select('*')
        ?.order('display_order', { ascending: true });

      if (parentId === null) {
        query?.is('parent_category_id', null);
      } else {
        query?.eq('parent_category_id', parentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching categories by parent:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get category by ID
  async getCategoryById(id) {
    try {
      const { data, error } = await supabase
        ?.from('material_categories')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching category:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get category hierarchy path (breadcrumb)
  async getCategoryPath(categoryId) {
    try {
      const path = [];
      let currentId = categoryId;

      while (currentId) {
        const { data, error } = await supabase
          ?.from('material_categories')
          ?.select('id, name, parent_category_id, level')
          ?.eq('id', currentId)
          ?.single();

        if (error) throw error;
        if (!data) break;

        path?.unshift(data);
        currentId = data?.parent_category_id;
      }

      return { data: path, error: null };
    } catch (error) {
      console.error('Error fetching category path:', error);
      return { data: null, error: error?.message };
    }
  }

  // Create new category
  async createCategory(categoryData) {
    try {
      const { data, error } = await supabase
        ?.from('material_categories')
        ?.insert([{
          name: categoryData?.name,
          description: categoryData?.description,
          parent_category_id: categoryData?.parentCategoryId || null,
          level: categoryData?.level || 0,
          display_order: categoryData?.displayOrder || 0,
          material_attributes: categoryData?.materialAttributes || false
        }])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating category:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update category
  async updateCategory(id, categoryData) {
    try {
      const updateData = {
        name: categoryData?.name,
        description: categoryData?.description,
        parent_category_id: categoryData?.parentCategoryId,
        level: categoryData?.level,
        display_order: categoryData?.displayOrder,
        material_attributes: categoryData?.materialAttributes,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('material_categories')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating category:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete category
  async deleteCategory(id) {
    try {
      const { error } = await supabase
        ?.from('material_categories')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting category:', error);
      return { error: error?.message };
    }
  }

  // Get category tree structure (hierarchical)
  async getCategoryTree() {
    try {
      const { data: allCategories, error } = await this.getAllCategories();
      if (error) throw new Error(error);

      // Build tree structure
      const categoryMap = {};
      const rootCategories = [];

      // First pass: create map
      allCategories?.forEach(category => {
        categoryMap[category?.id] = { ...category, children: [] };
      });

      // Second pass: build tree
      allCategories?.forEach(category => {
        if (category?.parent_category_id === null) {
          rootCategories?.push(categoryMap?.[category?.id]);
        } else if (categoryMap?.[category?.parent_category_id]) {
          categoryMap?.[category?.parent_category_id]?.children?.push(categoryMap?.[category?.id]);
        }
      });

      return { data: rootCategories, error: null };
    } catch (error) {
      console.error('Error building category tree:', error);
      return { data: null, error: error?.message };
    }
  }

  // Count materials linked to category
  async getMaterialCountByCategory(categoryId) {
    try {
      const { count, error } = await supabase
        ?.from('materials')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('material_category', categoryId);

      if (error) throw error;
      return { data: count, error: null };
    } catch (error) {
      console.error('Error counting materials:', error);
      return { data: 0, error: error?.message };
    }
  }
}

const materialCategoryService = new MaterialCategoryService();

export { materialCategoryService };
export default materialCategoryService;